import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ArrowLeft } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { fundService } from '@/services/fundService';
import { queryKeys } from '@/hooks/react-query/queryKeys';
import { RecipientStatusBadge } from './EmailStatusBadge';
import { renderSubject } from './EmailUtils';
import { formatDateTime } from '@/utils/formatters';
import {
  FundEmailBatchDetailResponse,
  FundInvestorEmailResponse,
} from '@/types/fund';

interface EmailDetailPanelProps {
  fundId: string;
  batchId: string;
}

export function EmailDetailPanel({ fundId, batchId }: EmailDetailPanelProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecipient, setSelectedRecipient] =
    useState<FundInvestorEmailResponse | null>(null);

  const { data: email, isLoading } = useQuery<FundEmailBatchDetailResponse>({
    queryKey: queryKeys.funds.emailDetail(fundId, batchId),
    queryFn: () => fundService.getFundEmailBatchDetails(fundId, batchId),
  });

  const aggregateStatus = useMemo(() => {
    if (!email?.recipients?.length) return null;

    const total = email.recipients.length;
    const sent = email.recipients.filter(
      (r: FundInvestorEmailResponse) => r.status.toLowerCase() === 'sent'
    ).length;
    const failed = email.recipients.filter(
      (r: FundInvestorEmailResponse) => r.status.toLowerCase() === 'failed'
    ).length;

    if (sent === total)
      return { label: 'All Delivered', color: 'text-green-600' };
    if (failed === total) return { label: 'Failed', color: 'text-destructive' };
    if (failed > 0)
      return {
        label: `${sent} of ${total} Delivered`,
        color: 'text-orange-600',
      };

    // Default processing state
    return { label: 'Processing...', color: 'text-blue-600' };
  }, [email]);

  const filteredRecipients = useMemo(() => {
    if (!email?.recipients) return [];
    return email.recipients.filter((recipient: FundInvestorEmailResponse) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        recipient.to_email.toLowerCase().includes(term) ||
        (recipient.to_name && recipient.to_name.toLowerCase().includes(term))
      );
    });
  }, [email, searchTerm]);

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="animate-pulse rounded-md bg-muted h-8 w-3/4" />
        <div className="animate-pulse rounded-md bg-muted h-4 w-1/2" />
        <div className="pt-4 space-y-2">
          <div className="animate-pulse rounded-md bg-muted h-20 w-full" />
          <div className="animate-pulse rounded-md bg-muted h-20 w-full" />
        </div>
      </div>
    );
  }

  if (!email)
    return (
      <div className="p-4 text-center text-muted-foreground">
        Detail not found
      </div>
    );

  // --- Recipient Email Preview View ---
  if (selectedRecipient) {
    return (
      <div className="flex flex-col h-full">
        {/* Preview Header */}
        <div className="p-4 border-b bg-muted/20 relative">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedRecipient(null)}
              className="h-8 w-8 p-0 shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <h3 className="font-semibold text-base leading-tight truncate">
                {selectedRecipient.to_name || 'Unknown'}
              </h3>
              <span className="text-xs text-muted-foreground">
                {selectedRecipient.to_email}
              </span>
            </div>
            <div className="ml-auto shrink-0">
              <RecipientStatusBadge
                status={selectedRecipient.status}
                error={selectedRecipient.error_message}
              />
            </div>
          </div>
        </div>

        {/* Email Preview */}
        <ScrollArea className="flex-1">
          <div className="p-6">
            <div className="max-w-[700px] mx-auto bg-card shadow-sm border rounded-lg overflow-hidden">
              {/* Fake Email Client Header */}
              <div className="bg-muted/50 border-b p-4 space-y-2">
                <div className="flex gap-2 text-sm">
                  <span className="text-muted-foreground w-16 text-right shrink-0">
                    To:
                  </span>
                  <span className="font-medium">
                    {selectedRecipient.to_name || 'Unknown'} &lt;
                    {selectedRecipient.to_email}&gt;
                  </span>
                </div>
                <div className="flex gap-2 text-sm">
                  <span className="text-muted-foreground w-16 text-right shrink-0">
                    Subject:
                  </span>
                  <span className="font-medium">
                    {selectedRecipient.rendered_subject ||
                      renderSubject(email.subject)}
                  </span>
                </div>
                {selectedRecipient.sent_at && (
                  <div className="flex gap-2 text-sm">
                    <span className="text-muted-foreground w-16 text-right shrink-0">
                      Date:
                    </span>
                    <span className="text-muted-foreground">
                      {formatDateTime(selectedRecipient.sent_at)}
                    </span>
                  </div>
                )}
              </div>

              {/* Email Content */}
              <div className="p-8 min-h-[400px]">
                {selectedRecipient.rendered_html_content ? (
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none"
                    // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- email HTML is rendered from our own backend Jinja templates with autoescape
                    dangerouslySetInnerHTML={{
                      __html: selectedRecipient.rendered_html_content,
                    }}
                  />
                ) : (
                  <div className="text-center text-muted-foreground py-12">
                    <p>Rendered email content is not available.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>
    );
  }

  // --- Recipients List View (Default) ---
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b bg-muted/20 relative">
        <h3 className="font-semibold text-base pr-8 mb-2 leading-tight">
          {renderSubject(email.subject)}
        </h3>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{formatDateTime(email.created_at)}</span>
          {aggregateStatus && (
            <span className={cn('font-medium', aggregateStatus.color)}>
              {aggregateStatus.label}
            </span>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="px-4 py-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search recipients..."
            className="pl-8 h-9 text-xs"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Recipients Table */}
      <div className="flex-1 overflow-auto">
        <Table>
          <TableHeader className="bg-muted/40 sticky top-0 z-10">
            <TableRow className="h-8 hover:bg-muted/40">
              <TableHead className="h-8 text-xs font-medium pl-4">
                Recipient
              </TableHead>
              <TableHead className="h-8 text-xs font-medium w-[80px] text-right pr-4">
                Status
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRecipients.map((recipient: FundInvestorEmailResponse) => (
              <TableRow
                key={recipient.id}
                className="h-12 border-b-0 hover:bg-muted/30 cursor-pointer transition-colors"
                onClick={() => setSelectedRecipient(recipient)}
              >
                <TableCell className="pl-4 py-2">
                  <div className="flex flex-col">
                    <span className="font-medium text-sm text-foreground">
                      {recipient.to_name || 'Unknown'}
                    </span>
                    <span
                      className="text-xs text-muted-foreground truncate max-w-[180px]"
                      title={recipient.to_email}
                    >
                      {recipient.to_email}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-right pr-4 py-2 align-top pt-3">
                  <RecipientStatusBadge
                    status={recipient.status}
                    error={recipient.error_message}
                  />
                </TableCell>
              </TableRow>
            ))}
            {filteredRecipients.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={2}
                  className="h-24 text-center text-xs text-muted-foreground"
                >
                  No recipients found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
